/**
 * Staff Allocation report helpers shared by the Reports screen (direct Excel
 * export) and the StaffAllocationPrint report page.
 *
 * Source API: GET Staff/StaffAllocation  (see Doc.md)
 * Each staff row carries `allocations` (class + section + subject). The report
 * shows one line per class/section of a staff member, listing the subjects
 * taught in that class/section so it is clear where each subject is taught.
 */

export const STAFF_ALLOCATION_EXPORT_COLUMNS = [
  { key: "name", label: "Staff Name" },
  { key: "designation", label: "Designation" },
  { key: "departmentName", label: "Department" },
  { key: "className", label: "Class" },
  { key: "sectionName", label: "Section" },
  { key: "subjects", label: "Subjects" },
  { key: "classTeacher", label: "Class Teacher" },
];

function uniqueInOrder(values) {
  const seen = new Set();
  return values.filter((value) => {
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

function classSectionKey(className, sectionName) {
  return `${className || ""}||${sectionName || ""}`;
}

/**
 * @param {Array} staffRows - `data` array from Staff/StaffAllocation
 * @param {{ departmentNames?: string[], classNames?: string[], sectionNames?: string[], subjectNames?: string[] }} filters
 * @returns {Array<{ staffID, name, designation, departmentName, sections: Array<{ className, sectionName, subjects, classTeacher }> }>}
 */
export function buildStaffAllocationGroups(staffRows, filters = {}) {
  const departmentNames = filters.departmentNames || [];
  const classNames = filters.classNames || [];
  const sectionNames = filters.sectionNames || [];
  const subjectNames = filters.subjectNames || [];

  return (Array.isArray(staffRows) ? staffRows : []).reduce((groups, staff) => {
    if (
      departmentNames.length > 0 &&
      !departmentNames.includes(staff.departmentName)
    ) {
      return groups;
    }

    const allocations = (staff.allocations || []).filter(
      (row) =>
        (classNames.length === 0 || classNames.includes(row.className)) &&
        (sectionNames.length === 0 ||
          sectionNames.includes(row.sectionName)) &&
        (subjectNames.length === 0 || subjectNames.includes(row.subjectName))
    );

    // Staff with no subject and no class/section assigned are not part of the report.
    if (allocations.length === 0) return groups;

    const sections = [];
    const sectionByKey = new Map();

    allocations.forEach((row) => {
      const key = classSectionKey(row.className, row.sectionName);
      let section = sectionByKey.get(key);
      if (!section) {
        section = {
          className: row.className || "",
          sectionName: row.sectionName || "",
          subjects: [],
          isClassTeacher: false,
        };
        sectionByKey.set(key, section);
        sections.push(section);
      }
      if (row.subjectName) section.subjects.push(row.subjectName);
      if (row.isClassTeacher) section.isClassTeacher = true;
    });

    // A class-only class-teacher entry (no section) covers every section of that class.
    (staff.classTeacherOf || []).forEach((entry) => {
      sections.forEach((section) => {
        if (entry.className !== section.className) return;
        if (entry.sectionName && entry.sectionName !== section.sectionName) {
          return;
        }
        section.isClassTeacher = true;
      });
    });

    groups.push({
      staffID: staff.staffID,
      name: staff.name || "",
      designation: staff.designation || "",
      departmentName: staff.departmentName || "",
      sections: sections.map((section) => ({
        className: section.className,
        sectionName: section.sectionName,
        subjects: uniqueInOrder(section.subjects).join(", "),
        classTeacher: section.isClassTeacher ? "Yes" : "No",
      })),
    });

    return groups;
  }, []);
}

/** One Excel row per staff + class/section, matching the printed report. */
export function flattenStaffAllocationRows(groups) {
  return (groups || []).flatMap((staff) =>
    (staff.sections || []).map((section) => ({
      name: staff.name,
      designation: staff.designation,
      departmentName: staff.departmentName,
      className: section.className,
      sectionName: section.sectionName,
      subjects: section.subjects,
      classTeacher: section.classTeacher,
    }))
  );
}
